/**
 * Resolve navigation target for a notification.
 * Prefer payload.link; type-specific cases can be added as emitters land.
 */
export function getNotificationPath(notification) {
  const payload = notification?.payload;
  if (payload && typeof payload.link === "string" && payload.link.startsWith("/")) {
    return payload.link;
  }

  const orgId = notification?.org_id ?? notification?.orgId ?? payload?.org_id;
  const type = notification?.type;

  if (type === "dm.message" || type === "channel.here" || type === "dm.reaction") {
    const conversationId =
      payload?.conversation_id ??
      notification.entity_id ??
      notification.entityId;
    if (orgId != null && conversationId != null) {
      const base = `/o/${encodeURIComponent(String(orgId))}/messages/${conversationId}`;
      const messageId = payload?.message_id;
      return messageId != null ? `${base}?message=${messageId}` : base;
    }
  }

  if (
    type === "post.critical" ||
    type === "work.mention" ||
    type === "post.comment" ||
    type === "comment.reply" ||
    type === "post.like" ||
    type === "work.assignee"
  ) {
    const postId = payload?.post_id ?? notification.entity_id ?? notification.entityId;
    if (orgId != null && postId != null) {
      const base = `/o/${encodeURIComponent(String(orgId))}/p/${postId}`;
      const commentId = payload?.comment_id;
      return commentId != null ? `${base}?comment=${commentId}` : base;
    }
  }

  if (type === "team.handoff") {
    const teamId = payload?.to_team_id;
    if (orgId != null && teamId != null) {
      return `/o/${encodeURIComponent(String(orgId))}/teams/${teamId}?tab=kanban&handoffs=1`;
    }
  }

  if (type === "invite.accepted") {
    const teamId = payload?.team_id;
    if (orgId != null && teamId != null) {
      return `/o/${encodeURIComponent(String(orgId))}/teams/${teamId}`;
    }
    if (orgId != null) {
      return `/orgs/${encodeURIComponent(String(orgId))}`;
    }
  }

  if (type === "team.join_request") {
    if (orgId != null) {
      return `/orgs/${encodeURIComponent(String(orgId))}`;
    }
  }

  return null;
}

function countLabel(n) {
  const c = Number(n) || 0;
  return c > 99 ? "99+" : String(c);
}

export function getNotificationTitle(notification) {
  const payload = notification?.payload;
  const type = notification?.type;
  const count = Number(payload?.count) || 0;

  if (type === "dm.message" && count > 1) {
    const name = payload.sender_name || "Someone";
    return `${countLabel(count)} messages from ${name}`;
  }
  if (type === "post.comment" && count > 1) {
    return `${countLabel(count)} comments on your post`;
  }
  if (type === "comment.reply" && count > 1) {
    return `${countLabel(count)} replies to your comment`;
  }
  if (type === "post.like" && count > 1) {
    return `${countLabel(count)} likes on your post`;
  }
  if (type === "channel.here" && count > 1) {
    return `${countLabel(count)} @here mentions`;
  }

  if (payload?.title) return String(payload.title);

  const fallbacks = {
    "post.comment": "New comment on your post",
    "comment.reply": "Reply to your comment",
    "post.like": "Someone liked your post",
    "work.assignee": "You were assigned",
    "team.handoff": "Card handed off to your team",
    "invite.accepted": "Invitation accepted",
    "channel.here": "@here mention",
  };
  return fallbacks[type] || type || "Notification";
}

export function getNotificationBody(notification) {
  const payload = notification?.payload;
  if (payload?.body) return String(payload.body);
  return "";
}

/** True when notification should prefer opening the chat drawer on desktop. */
export function shouldOpenDmDrawer(notification) {
  return (
    notification?.type === "dm.message" ||
    notification?.type === "channel.here" ||
    notification?.type === "dm.reaction"
  );
}
